import React, { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  ModalVariant,
  Form,
  FormGroup,
  Popover,
  TextInput,
  Spinner,
  ValidatedOptions,
} from '@patternfly/react-core';
import { HelpIcon, ExclamationCircleIcon } from '@patternfly/react-icons';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { nanoid } from 'nanoid';
import { ClusterRepresentation } from 'src/query/deployments';
import { useValidDeploymentName } from '@app/components/NamesValid';
import { IconDeployment } from '@app/components/icons/icon-deployment';
import ButtonCustom from './button';
import { createClusterDedicatedDeployment } from 'src/api/orgs';

type Props = {
  refresh: (val: string) => void;
  cluster: ClusterRepresentation;
};

export const CreateDedicatedDeployment: React.FC<Props> = ({ refresh, cluster }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const clusterIsActive = cluster?.status === 'ACTIVE';
  const { checkDedicatedClusterDeploymentNameAvailability, nameValidityResponseText, clearNameValid } =
    useValidDeploymentName();
  const { isNameValid, nameHelperText } = nameValidityResponseText();
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
  } = useForm();

  useEffect(() => {
    if (isModalOpen === false) {
      clearNameValid();
      reset();
    }
  }, [isModalOpen]);

  const handleModalToggle = () => {
    setIsModalOpen(false);
  };

  const resetForm = () => {
    reset();
    clearNameValid();
  };

  const submitForm = async (dep) => {
    const { name } = dep;

    if (!isNameValid) {
      toast.error(`Deployment name is not valid.`);
    }
    try {
      toast('Creating deployment. Please standby as this can take a few moments...');

      await createClusterDedicatedDeployment({ name }, cluster.id!);

      toast.success(`${name} deployment created successfully.`);
      setIsModalOpen(false);
      refresh(name + nanoid());
      resetForm();
      return;
    } catch (e) {
      // TODO: the context of the resp error is not coming through due to how the "Fetcher" is configured
      // figure out how to get that to be passed through
      toast.error(`${name} deployment was not able to be created.`);
      console.log('[Error] Not able to create the deployment.', e);
    }
  };

  // Filter only orgs that user has the right role for
  // const manageRoleOrgs = orgs.filter((org) => checkUserRoleForOrg('manage-organization', org.id));

  const descriptionText = () => {
    let clusterText = <></>;
    if (isModalOpen) {
      clusterText = (
        <div style={{ marginTop: '.5rem' }}>
          This will create a deployment that will deploy into the <b>{cluster.name}</b> cluster. If you do not wish for
          this to be the case, please{' '}
          <Button onClick={resetForm} isInline variant="link">
            reset
          </Button>{' '}
          the form.
        </div>
      );
    }
    return clusterText;
  };

  let modalTitle = 'Create Dedicated Deployment';
  if (cluster?.name) {
    modalTitle = (
      <span>
        Create Dedicated Deployment for <b>{cluster.name}</b> Cluster
      </span>
    );
  }

  return (
    <>
      <ButtonCustom
        isDisabled={!clusterIsActive}
        onClick={() => setIsModalOpen(true)}
        light
        content={
          <>
            <IconDeployment height="28px" width="28px" /> <div>Create Deployment</div>
          </>
        }
      />
      {isModalOpen && (
        <Modal
          key={cluster.id}
          id={cluster.id}
          variant={ModalVariant.medium}
          title={modalTitle}
          description={descriptionText()}
          isOpen={!!isModalOpen}
          onClose={handleModalToggle}
        >
          <Form
            id="modal-with-form-create-deployment"
            onSubmit={handleSubmit(submitForm)}
            style={{ marginTop: '2rem' }}
          >
            {/* <FormGroup
            label="Cluster"
            labelIcon={
              <Popover headerContent={<div>Cluster</div>} bodyContent={<div>Cluster to deploy into.</div>}>
                <button
                  type="button"
                  aria-label="More info for cluster feld"
                  onClick={(e) => e.preventDefault()}
                  aria-describedby="modal-with-form-form-cluster"
                  className="pf-c-form__group-label-help"
                >
                  <HelpIcon noVerticalAlign />
                </button>
              </Popover>
            }
            isRequired
            fieldId="modal-with-form-form-cluster"
          >
            <Controller
              name="cluster"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <FormSelect
                  id="cluster-choose-form"
                  aria-label="Choose a cluster"
                  isDisabled={isObject(isModalOpen)}
                  {...field}
                >
                  <FormSelectOption value="" label="Select one" isDisabled isPlaceholder />
                  { {clusters.map((cluster) => (
                  <FormSelectOption key={cluster.id} value={cluster.id} label={`${cluster.name} (${cluster.region})`} />
                  ))}
                </FormSelect>
              )}
            />
          </FormGroup> */}
            <FormGroup
              label="Name"
              labelIcon={
                <Popover
                  headerContent={<div>Name</div>}
                  bodyContent={<div>The name of the realm. This must be unique across all Phase Two Deployments.</div>}
                >
                  <button
                    type="button"
                    aria-label="More info for name field"
                    onClick={(e) => e.preventDefault()}
                    aria-describedby="modal-with-form-form-name"
                    className="pf-c-form__group-label-help"
                  >
                    <HelpIcon noVerticalAlign />
                  </button>
                </Popover>
              }
              isRequired
              fieldId="modal-with-form-form-name"
              validated={isNameValid}
              helperText={nameHelperText}
              helperTextInvalid={nameHelperText}
              helperTextInvalidIcon={<ExclamationCircleIcon />}
            >
              <Controller
                name="name"
                control={control}
                rules={{ required: true, min: 3 }}
                render={({ field }) => (
                  <TextInput
                    id="modal-with-form-form-name"
                    isRequired
                    type="text"
                    validated={isNameValid}
                    onKeyUp={(e) => checkDedicatedClusterDeploymentNameAvailability(e.target.value, cluster.id)}
                    {...field}
                  />
                )}
              />
            </FormGroup>
            <footer className="pf-c-modal-box__footer" style={{ paddingRight: 0, paddingLeft: 0, paddingBottom: 0 }}>
              <Button
                key="create"
                variant="primary"
                form="modal-with-form-create-deployment"
                isDisabled={
                  isSubmitting || isNameValid === ValidatedOptions.default || isNameValid === ValidatedOptions.error
                }
                type="submit"
                // onClick={() => submitForm()}
              >
                {isSubmitting ? (
                  <div>
                    <Spinner
                      isSVG
                      size="md"
                      aria-label="Contents of the small example"
                      style={{ '--pf-c-spinner--Color': 'white' }}
                    />{' '}
                    Creating deployment...
                  </div>
                ) : (
                  'Confirm'
                )}
              </Button>
              <Button key="cancel" variant="link" onClick={handleModalToggle}>
                Cancel
              </Button>
              <Button key="reset" variant="link" onClick={resetForm} style={{ marginLeft: 'auto' }}>
                Reset
              </Button>
            </footer>
          </Form>
        </Modal>
      )}
    </>
  );
};
